<template>
  <b-form-group :label="title || null" label-class="mb-1">
    <v-select
      :value="currentValue"
      :options="processedOptions"
      :disabled="disabled"
      :searchable="true"
      :clearable="clearable"
      :placeholder="placeholder"
      class="searchable-dropdown"
      :selectable="isOptionSelectable"
      label="title"
      @input="changeState"
    >
      <template #option="option">
        <span v-if="option.isGroup" class="font-weight-bold">{{
          option.title
        }}</span>
        <span v-else :class="{ 'ml-3': option.isGrouped }">
          {{ option.title }}
        </span>
      </template>

      <template #selected-option="option">
        {{ option.title }}
      </template>

      <template #no-options="{ search }">
        <span v-if="search">No results found for "{{ search }}"</span>
        <span v-else>No options available</span>
      </template>
    </v-select>
  </b-form-group>
</template>

<script>
import vSelect from "vue-select";

export default {
  name: "SearchableDropdown",
  components: {
    vSelect,
  },
  props: {
    options: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    clearable: {
      type:Boolean,
      required:false,
      default:true
    },
    value: {
      type: [String, Number, Object],
      required: false,
      default: null,
    },
    placeholder: {
      type: String,
      required: false,
      default: "Search and select...",
    },
    groupBy: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    currentValue: {
      get() {
        if (this.value === null || this.value === undefined) {
          return null;
        }

        // If value is a primitive, find the matching option
        if (typeof this.value === "string" || typeof this.value === "number") {
          return (
            this.processedOptions.find(
              (option) => !option.isGroup && option.value === this.value
            ) || null
          );
        }

        // If value is already an object, return it
        return this.value;
      },
      set(newValue) {
        // When v-select updates the value, handle it through changeState
        this.changeState(newValue);
      }
    },
    processedOptions() {
      if (!this.groupBy) return this.options;
      
      const groups = {};
      const ungrouped = [];
      
      this.options.forEach(option => {
        const group = option[this.groupBy]?.trim();
        if (group) {
          if (!groups[group]) groups[group] = { items: [], order: option.categoryOrder || 999 };
          groups[group].items.push({ ...option, isGrouped: true });
          groups[group].order = Math.min(groups[group].order, option.categoryOrder || 999);
        } else {
          ungrouped.push(option);
        }
      });
      
      return [
        ...ungrouped,
        ...Object.entries(groups)
          .sort(([, a], [, b]) => a.order - b.order)
          .flatMap(([name, { items }]) => [
            { title: name, value: `__group_${name}`, isGroup: true },
            ...items
          ])
      ];
    },
  },
  methods: {
    isOptionSelectable(option) {
      // Group headers should not be selectable
      return !option.isGroup;
    },
    changeState(option) {
      // Don't allow selection of group headers
      if (option && option.isGroup) {
        return;
      }

      // Emit the value, not the full option object (for compatibility with existing code)
      const value = option ? option.value : null;
      this.$emit("input", value);
    },
  },
};
</script>

<style>
/* Style grouped options with left padding */
.searchable-dropdown .ml-3 {
  margin-left: 1rem;
}

/* Ensure all vue-select components use full container width */
.searchable-dropdown .v-select {
  width: 100%;
  font-size: 1rem !important;
}

/* Make dropdown menu match the width of the toggle */
.searchable-dropdown .vs__dropdown-menu {
  min-width: 100%;
  width: 100%;
}

/* Ensure dropdown options don't wrap unnecessarily */
.searchable-dropdown .vs__dropdown-option {
  white-space: nowrap;
}
</style>
